import React, { useState, useEffect, useRef, useMemo } from "react";
import { AiOutlineClose } from "react-icons/ai";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Form, Spinner } from "react-bootstrap";
import { TechnicianAddressType } from "../../models/jobs";
import UseWindowSize from "../../hooks/helpers/useWindowSize";
import { clone, onelineAddress, onelineAddressWithoutComma } from "../../utils/util";
import { AddressModalWrapper } from "../../styles/checkout-page-style";
import { Address } from "../../models/user";

const schema = yup.object({
  addressLine1: yup.string().required(),
  addressLine2: yup.string(),
  city: yup.string().required(),
  state: yup.string().required(),
  zipCode: yup.string().required().matches(
    /^[0-9]+$/,
    'Zip code must be a number'
  ),
  country: yup.string().required(),
});


export default function AddressModal({
  address,
  isBusy,
  closeModal,
  saveAddress,
}: {
  address: Address | undefined;
  isBusy: boolean;
  closeModal: () => void;
  saveAddress: (address: Address) => Promise<void>;
}) {
  const addressSelectionType = "new";
  const [country, setSelectedCountry] = useState<string>("USA");
  const [state, setSelectedState] = useState<string>("");
  const [technicianAddressId, setTechnicianAddressId] = useState<string>("");
  const [addressLine1, setAddressLine1] = useState<string>("");
  const [addressLine2, setAddressLine2] = useState<string>("");
  const [city, setCity] = useState<string>("");
  const [zipCode, setZipCode] = useState<string>("");
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const screenProps = UseWindowSize();

  const {
    register,
    handleSubmit,
    formState: { isDirty, errors },
  } = useForm<Address>(addressSelectionType != "new" || 'old' ? {
    resolver: yupResolver(schema),
    defaultValues: {
      technicianAddressId: address?.technicianAddressId,
      addressLine1: address?.addressLine1,
      addressLine2: address?.addressLine2,
      city: address?.city,
      country: address?.country,
      state: address?.state,
      zipCode: address?.zipCode,
      phoneNumber: address?.phoneNumber,
    },
  } : { resolver: yupResolver(schema) });

  useEffect(() => {
    if (address) {
      setSelectedState(address?.state ?? "");
      setZipCode(address?.zipCode ?? "");
      setAddressLine1(address?.addressLine1 ?? "");
      setAddressLine2(address?.addressLine2 ?? "");
      setCity(address?.city ?? "");
      setSelectedCountry(address?.country ?? "");
      setTechnicianAddressId(address?.technicianAddressId ?? "");
    }

  }, [address]);

  const onSubmit = async (data: any) => {
    const clonedEditedAddress = clone<TechnicianAddressType>(data as TechnicianAddressType);
    if (addressSelectionType == "new") { delete clonedEditedAddress?.technicianAddressId }; // force this to be created as new address
    const oneLineAddress = await onelineAddressWithoutComma(clonedEditedAddress);
    const { lat, lng } = await getCoordinates(oneLineAddress);
    await saveAddress({
      ...clonedEditedAddress,
      latitude: lat,
      longitude: lng,
      technicianAddressId: technicianAddressId?.length > 0 ? technicianAddressId : new Date().toISOString()
    });
    closeModal();
  };

  async function getCoordinates(address: string) {
    let lat, lng;
    let url = "https://maps.googleapis.com/maps/api/geocode/json?address=" + address + '&key=' + "AIzaSyANLVyA_9PQMQyWYLaaTK8z8WIFZa3UUXk";
    await fetch(url)
      .then(response => response.json())
      .then(data => {
        lat = data?.results?.[0]?.geometry?.location?.lat;
        lng = data?.results?.[0]?.geometry?.location?.lng;
      })
    return { lat, lng }
  }

  return (
    <AddressModalWrapper>
      <div className="position-absolute close">
        <AiOutlineClose size={18} color="#000" onClick={closeModal} />
      </div>
      <div className="row" style={{ maxHeight: (screenProps.windowSize.height ?? 100) - 100, overflowY: "scroll" }}>
        <div className="col-md-12">
          <div className="addressForm mt-3 mx-md-auto p-md-4 ">
            <form onSubmit={handleSubmit(onSubmit)}>
              {isBusy && (
                <Spinner animation="border" />
              )}
              {!isBusy && (
                <>
                  <div className="mb-3">
                    <label style={{ fontSize: "24px", fontWeight: 600 }}>
                      {addressSelectionType == "new" ?
                        "Add a new address"
                        : "Select an address"
                      }
                    </label>

                  </div>

                  {addressSelectionType === "new" || addressSelectionType === "edit" ? (
                    <>
                      <div className="form-group my-3">
                        <label htmlFor="country">Country </label>
                        <br />
                        <select
                          // name="country"
                          className="form-select "
                          {...register("country")}
                          defaultValue={address?.country}
                        >
                          <option selected value={"USA"}>
                            United States{" "}
                          </option>
                          {/* {options.map((value: any, index: any) => (

                            <option key={index} value={value.value}>
                              {value.label}
                            </option>
                          ))} */}
                        </select>
                        <p className={"text-error"}>
                          {errors.country?.message}
                        </p>
                      </div>
                      <div className="form-group my-3">
                        <label htmlFor="address1">Address</label>
                        <input
                          type="text"
                          className="form-control mb-2"
                          id="address1"
                          aria-describedby="address1"
                          {...register("addressLine1", { required: true })}
                          defaultValue={address?.addressLine1}
                        />
                        <p className={"text-error"}>
                          {errors.addressLine1?.message}
                        </p>
                        <input
                          type="text"
                          className="form-control"
                          id="address2"
                          aria-describedby="address2"
                          defaultValue={address?.addressLine2}
                          {...register("addressLine2")}
                        />
                      </div>
                      <div className="row my-3">
                        <div className="col-sm-4">
                          <label>City</label>
                          <input
                            type="text"
                            className="form-control"
                            defaultValue={address?.city}
                            {...register("city", { required: true })}
                          />
                          <p className={"text-error"}>{errors.city?.message}</p>
                        </div>
                        <div className="col-sm-4">
                          <label>State</label>
                          {country != 'USA' ? <input
                            type="text"
                            className="form-control"
                            defaultValue={address?.state}
                            {...register("state", { required: true })}
                          /> :
                            <select
                              className="form-control custom-select"
                              defaultValue={address?.state}
                              {...register("state", { required: true })}
                            >
                              <option value="AL">Alabama</option>
                              <option value="AK">Alaska</option>
                              <option value="AZ">Arizona</option>
                              <option value="AR">Arkansas</option>
                              <option value="CA">California</option>
                              <option value="CO">Colorado</option>
                              <option value="CT">Connecticut</option>
                              <option value="DE">Delaware</option>
                              <option value="DC">District Of Columbia</option>
                              <option value="FL">Florida</option>
                              <option value="GA">Georgia</option>
                              <option value="HI">Hawaii</option>
                              <option value="ID">Idaho</option>
                              <option value="IL">Illinois</option>
                              <option value="IN">Indiana</option>
                              <option value="IA">Iowa</option>
                              <option value="KS">Kansas</option>
                              <option value="KY">Kentucky</option>
                              <option value="LA">Louisiana</option>
                              <option value="ME">Maine</option>
                              <option value="MD">Maryland</option>
                              <option value="MA">Massachusetts</option>
                              <option value="MI">Michigan</option>
                              <option value="MN">Minnesota</option>
                              <option value="MS">Mississippi</option>
                              <option value="MO">Missouri</option>
                              <option value="MT">Montana</option>
                              <option value="NE">Nebraska</option>
                              <option value="NV">Nevada</option>
                              <option value="NH">New Hampshire</option>
                              <option value="NJ">New Jersey</option>
                              <option value="NM">New Mexico</option>
                              <option value="NY">New York</option>
                              <option value="NC">North Carolina</option>
                              <option value="ND">North Dakota</option>
                              <option value="OH">Ohio</option>
                              <option value="OK">Oklahoma</option>
                              <option value="OR">Oregon</option>
                              <option value="PA">Pennsylvania</option>
                              <option value="RI">Rhode Island</option>
                              <option value="SC">South Carolina</option>
                              <option value="SD">South Dakota</option>
                              <option value="TN">Tennessee</option>
                              <option value="TX">Texas</option>
                              <option value="UT">Utah</option>
                              <option value="VT">Vermont</option>
                              <option value="VA">Virginia</option>
                              <option value="WA">Washington</option>
                              <option value="WV">West Virginia</option>
                              <option value="WI">Wisconsin</option>
                              <option value="WY">Wyoming</option>
                              <option value="AS">American Samoa</option>
                              <option value="GU">Guam</option>
                              <option value="MP">Northern Mariana Islands</option>
                              <option value="PR">Puerto Rico</option>
                              <option value="UM">
                                United States Minor Outlying Islands
                              </option>
                              <option value="VI">Virgin Islands</option>
                              <option value="AA">Armed Forces Americas</option>
                              <option value="AP">Armed Forces Pacific</option>
                              <option value="AE">Armed Forces Others</option>
                            </select>}
                          <p className={"text-error"}>
                            {errors.state?.message}
                          </p>
                        </div>
                        <div className="col-sm-4">
                          <label>Zip code</label>
                          <input
                            type="text"
                            className="form-control"
                            {...register("zipCode", { required: true })}
                            defaultValue={address?.zipCode}
                          />
                          <p className={"text-error"}>{errors.zipCode?.message}</p>
                        </div>
                      </div>
                    </>
                  ) : null}
                </>
              )}
              {!isBusy && <input
                type="submit"
                className="btn blue-bg text-white px-4"
                value="Save Address"
                onClick={handleSubmit(onSubmit)}
              />}
            </form>
          </div>
        </div>
      </div>
    </AddressModalWrapper>
  );
}
